import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { auth, signInWithBackend } from '@/services';

export type RequiredSessionStatus = 'checking' | 'ready' | 'error';

export function useRequiredSession() {
  const { isAuthenticated, isLoading, sessionError, signedOut, ensureUser, user, updateUser } = useAuth();
  const [hasChecked, setHasChecked] = useState(false);
  const [signInThrottled, setSignInThrottled] = useState(false);
  const hasStartedSignIn = useRef(false);

  useEffect(() => {
    let mounted = true;

    ensureUser().finally(() => {
      if (mounted) {
        setHasChecked(true);
      }
    });

    return () => {
      mounted = false;
    };
  }, [ensureUser]);

  useEffect(() => {
    if (!hasChecked || isLoading || isAuthenticated || sessionError || hasStartedSignIn.current) {
      return;
    }

    if (signedOut) {
      return;
    }

    if (auth.isAutoSignInThrottled()) {
      setSignInThrottled(true);
      return;
    }

    hasStartedSignIn.current = true;
    auth.markAutoSignIn();
    signInWithBackend(window.location.href, { signup: auth.isSignupRequested(), replace: true });
  }, [hasChecked, isAuthenticated, isLoading, sessionError, signedOut]);

  let status: RequiredSessionStatus;
  if (!hasChecked || isLoading) {
    status = 'checking';
  } else if (isAuthenticated) {
    status = 'ready';
  } else if (!signedOut && (sessionError || signInThrottled)) {
    status = 'error';
  } else {
    status = 'checking';
  }

  const retry = () => {
    if (signInThrottled) {
      signInWithBackend(window.location.href, { signup: auth.isSignupRequested() });
      return;
    }

    void ensureUser();
  };

  return { status, user, updateUser, retry };
}
